import React from "react";
import {
  Stack,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { usePromotion } from "@quest_editor/mocks";
import { ProfileType, TemplateScopeValue } from "@quest_editor/constants";
import { getTemplateScopeOptions } from "@quest_editor/utils/view/constLabels";
import { getPromotionLabels } from "@quest_editor/utils/view/promotionLabels";
import BasicInfoSection from "./BasicInfoSection";
import OptionSection from "./OptionSection";

type OptionsState = {
  dataCheck: boolean;
  dataCheckPriority: boolean;
  marriage: boolean;
  occupation: boolean;
  industry: boolean;
  numberOfPeople: boolean;
  numberOfChildren: boolean;
  templating: boolean;
  publicationRange: TemplateScopeValue | "";
};

const PromotionEdit = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: promotionId } = useParams<{ id: string }>();

  const { data: promotionData } = usePromotion(promotionId);
  const promotion = promotionData?.promotion;

  const templateScopes = getTemplateScopeOptions();
  const { getTypeLabel } = getPromotionLabels();

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [options, setOptions] = React.useState<OptionsState>({
    dataCheck: false,
    dataCheckPriority: false,
    marriage: false,
    occupation: false,
    industry: false,
    numberOfPeople: false,
    numberOfChildren: false,
    templating: false,
    publicationRange: "",
  });

  React.useEffect(() => {
    if (promotion) {
      setName(promotion.name);
      setDescription(promotion.description);
      setOptions({
        dataCheck: promotion.options.dataCheck,
        dataCheckPriority: promotion.options.dataCheckPriority,
        marriage: promotion.options.profiles.includes(ProfileType.MARRIAGE),
        occupation: promotion.options.profiles.includes(ProfileType.OCCUPATION),
        industry: promotion.options.profiles.includes(ProfileType.INDUSTRY),
        numberOfPeople: promotion.options.profiles.includes(
          ProfileType.NUMBER_OF_PEOPLE,
        ),
        numberOfChildren: promotion.options.profiles.includes(
          ProfileType.NUMBER_OF_CHILDREN,
        ),
        templating: promotion.options.template,
        publicationRange: promotion.options.templateScope ?? "",
      });
    }
  }, [promotion]);

  const handleCheckboxChange =
    (key: keyof OptionsState) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setOptions((prev: OptionsState) => ({
        ...prev,
        [key]: e.target.checked,
      }));
    };

  const handleSelectChange = (e: SelectChangeEvent<TemplateScopeValue | "">) =>
    setOptions((prev: OptionsState) => ({
      ...prev,
      publicationRange: e.target.value as TemplateScopeValue | "",
    }));

  const handleEdit = () => {
    navigate(`/quest_editor/promotion/${promotionId}`);
  };

  if (!promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={2}>
      <Typography variant="h6" component="h2">
        {promotion.name}
      </Typography>

      <BasicInfoSection
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        typeLabel={getTypeLabel(promotion.type)}
      />

      <OptionSection
        options={options}
        onCheckboxChange={handleCheckboxChange}
        onSelectChange={handleSelectChange}
        templateScopes={templateScopes}
      />

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        alignItems="center"
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to="/quest_editor"
          sx={{ position: "absolute", left: 0 }}
        >
          {t("promotion_edit.button.back")}
        </BackButton>
        <ActionButton onClick={handleEdit}>
          {t("promotion_edit.button.edit")}
        </ActionButton>
      </Stack>
    </Stack>
  );
};
export default PromotionEdit;
